import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { getCustomerDisplayName } from "@redotech/redo-model/customer";
import { memo, useContext, useState } from "react";
import { ActiveConversationContext } from "../context/active-conversation-context";
import { MergeModal } from "../conversation-actions/merge-modal";
import { MergeSuggestionDialog } from "./merge-suggestion-dialog";
import { dismissMergeSuggestionForConversation } from "./utils";

export const MergeSuggestionFlow = memo(function MergeSuggestionFlow({
  activeConversation,
  suggestedConversations,
  initialStep,
  onClose,
  alreadyDismissed,
}: {
  activeConversation: MinimalConversation;
  suggestedConversations: MinimalConversation[];
  initialStep: "dialog" | "merge";
  onClose: () => void;
  alreadyDismissed: boolean;
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const { setActiveConversation } = useContext(ActiveConversationContext);
  const [step, setStep] = useState<"dialog" | "merge">(initialStep);
  const [onBack, setOnBack] = useState<(() => boolean) | undefined>(undefined);
  const [selectedConversationIds, setSelectedConversationIds] = useState<
    Set<string>
  >(new Set([activeConversation._id]));
  const onCancel = () => {
    onClose();
    // Mark merge suggestion viewed in db
    if (!alreadyDismissed) {
      void dismissMergeSuggestionForConversation({
        client,
        conversationId: activeConversation._id,
        signal: new AbortController().signal,
      });
    }
  };

  return (
    <>
      {step === "dialog" && (
        <MergeSuggestionDialog
          customerName={getCustomerDisplayName(activeConversation.customer)}
          onCancel={onCancel}
          openMergeModal={() => {
            setSelectedConversationIds(
              new Set([
                activeConversation._id,
                ...suggestedConversations.map(
                  (conversation) => conversation._id,
                ),
              ]),
            );
            setOnBack(() => () => {
              onClose();
              return false;
            });
            setStep("merge");
          }}
          openMergeModalWithSuggestions={() => {
            setSelectedConversationIds(new Set([activeConversation._id]));
            setOnBack(undefined);
            setStep("merge");
          }}
        />
      )}
      {step === "merge" && (
        <MergeModal
          conversations={[activeConversation, ...suggestedConversations].filter(
            (conversation) => selectedConversationIds.has(conversation._id),
          )}
          onBack={onBack}
          open
          setActiveConversation={setActiveConversation}
          setOpen={(e) => !e && onCancel()}
          suggestedConversations={suggestedConversations}
        />
      )}
    </>
  );
});
